import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { ServicesIcon, SparkIcon, PlusIcon, CloseIcon } from '@/components/Icons';
import { StatMetric } from '@/components/StatMetric';
import { Modal } from '@/components/Modal';
import { useSessionState } from '@/context/SessionContext';
import { useServiceCatalogState } from '@/context/ServiceContext';
import { formatInrCurrency } from '@/lib/format';
import { serviceFormSchema, flattenFieldErrors, getFieldErrorsFromApiError } from '@/lib/validation';
import type { ServiceCatalogEntry, ServiceDraft, ServiceUpdate } from '@/lib/types';

type ServiceFormState = {
  name: string;
  reminderDays: string;
  amount: string;
};

export function ServicesPage() {
  const { sessionRole, isEnterprise, isReady: sessionReady } = useSessionState();
  const { isReady, error, services, addService, updateService, deleteService } = useServiceCatalogState();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ServiceFormState>({ name: '', reminderDays: '', amount: '' });
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const serviceCount = services.length;
  const averageReminderDays = serviceCount
    ? Math.round(services.reduce((sum, service) => sum + service.reminderDays, 0) / serviceCount)
    : 0;
  const totalValue = services.reduce((sum, service) => sum + service.amount, 0);

  const sortedServices = useMemo(
    () => [...services].sort((left, right) => right.amount - left.amount),
    [services]
  );

  function openAddForm() {
    setEditingId(null);
    setForm({
      name: '',
      reminderDays: '30',
      amount: '',
    });
    setFeedback(null);
    setFieldErrors({});
    setIsFormOpen(true);
  }

  function openEditForm(service: ServiceCatalogEntry) {
    setEditingId(service.id);
    setForm({
      name: service.name,
      reminderDays: String(service.reminderDays),
      amount: String(service.amount),
    });
    setFeedback(null);
    setFieldErrors({});
    setIsFormOpen(true);
  }

  async function handleSave() {
    try {
      const validation = serviceFormSchema.safeParse(form);

      if (!validation.success) {
        setFieldErrors(flattenFieldErrors(validation.error.flatten().fieldErrors));
        setFeedback(null);
        return;
      }

      const payload = validation.data;
      setFieldErrors({});

      if (editingId) {
        const result = await updateService(editingId, payload satisfies ServiceUpdate);
        setFeedback({ kind: 'success', message: `${result.name} updated.` });
      } else {
        const result = await addService(payload satisfies ServiceDraft);
        setFeedback({ kind: 'success', message: `${result.name} added.` });
      }

      setIsFormOpen(false);
      setEditingId(null);
      setForm({ name: '', reminderDays: '30', amount: '' });
    } catch (saveError) {
      const apiFieldErrors = getFieldErrorsFromApiError(saveError);

      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...apiFieldErrors }));
        setFeedback(null);
        return;
      }

      if (saveError instanceof Error && /service with this name already exists/i.test(saveError.message)) {
        setFieldErrors({ name: saveError.message });
        setFeedback(null);
        return;
      }

      setFeedback({
        kind: 'error',
        message: saveError instanceof Error && saveError.message ? saveError.message : 'Could not save the service.',
      });
    }
  }

  async function handleDelete() {
    if (!deleteTargetId) {
      return;
    }

    try {
      const removed = await deleteService(deleteTargetId);
      if (removed) {
        setFeedback({ kind: 'success', message: 'Service deleted.' });
      }
    } catch (error) {
      setFeedback({
        kind: 'error',
        message: error instanceof Error && error.message ? error.message : 'Could not delete the service.',
      });
    } finally {
      setDeleteTargetId(null);
    }
  }

  if (!sessionReady) {
    return <div className="section">Loading services...</div>;
  }

  if (sessionRole === null) {
    return <Navigate to="/login" replace />;
  }

  if (!isEnterprise) {
    return <Navigate to="/settings" replace />;
  }

  if (!isReady) {
    return <div className="section">Loading services...</div>;
  }

  return (
    <div className="section page-grid">
      {feedback ? <div className={`notice ${feedback.kind}`}>{feedback.message}</div> : null}
      {error ? <div className="notice error">{error}</div> : null}

      <section className="hero-layout">
        <div className="hero-copy">
          <span className="eyebrow">
            <ServicesIcon size={14} />
            <strong>Services</strong>
            <span>{serviceCount} records</span>
          </span>

          <h1 className="display-title">
            <span>Service</span>
            <span>catalog</span>
          </h1>

          <p className="copy">Manage the shared service catalog with create, edit, and delete flows.</p>

          <div className="button-row hero-actions">
            <button className="primary-button" type="button" onClick={openAddForm}>
              <SparkIcon size={16} />
              Add service
            </button>
          </div>
        </div>

        <div className="stats-grid">
          <StatMetric label="Services" value={String(serviceCount)} hint="Editable service records" />
          <StatMetric label="Reminder" value={`${averageReminderDays} days`} hint="Average reminder cadence" />
          <StatMetric label="Value" value={formatInrCurrency(totalValue)} hint="Total catalog value" />
          <StatMetric label="Scope" value="Admin" hint="Hidden from staff sessions" />
        </div>
      </section>

      <section className="content-grid">
        <div className="stack">
          <div className="panel">
            <div className="section-heading">
              <div>
                <h2>Catalog</h2>
                <p>Tap a service to edit it. Delete actions are protected by confirmation.</p>
              </div>
            </div>

            <div className="list">
              {sortedServices.map((service) => (
                <div
                  key={service.id}
                  className="list-item interactive"
                  role="button"
                  tabIndex={0}
                  onClick={() => openEditForm(service)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openEditForm(service);
                    }
                  }}
                  >
                  <div>
                    <strong>{service.name}</strong>
                    <span>{service.reminderDays} reminder days</span>
                    <p>{service.id}</p>
                  </div>
                  <div className="item-meta">
                    <strong>{formatInrCurrency(service.amount)}</strong>
                    <button
                      className="ghost-button"
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setDeleteTargetId(service.id);
                      }}>
                      <CloseIcon size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="stack">
          <div className="panel">
            <div className="section-heading">
              <div>
                <h2>Highest value items</h2>
                <p>A quick view of the most expensive services in the catalog.</p>
              </div>
            </div>

            <div className="list">
              {sortedServices.slice(0, 4).map((service) => (
                <div className="list-item" key={service.id}>
                  <div>
                    <strong>{service.name}</strong>
                    <span>{service.reminderDays} days</span>
                  </div>
                  <strong>{formatInrCurrency(service.amount)}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Modal
        open={isFormOpen}
        title={editingId ? 'Edit service' : 'Add service'}
        description="Create or update the service records used across the salon app."
        onClose={() => setIsFormOpen(false)}
        wide>
        <div className="field-grid form-stack">
          <label className="field">
            <span className="field-label">Service name</span>
            <input
              className="input"
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              placeholder="Haircut"
            />
            {fieldErrors.name ? <span className="field-error">{fieldErrors.name}</span> : null}
          </label>

          <div className="split-grid">
            <label className="field">
              <span className="field-label">Reminder days</span>
              <input
                className="input"
                value={form.reminderDays}
                onChange={(event) => setForm((current) => ({ ...current, reminderDays: event.target.value.replace(/\D/g, '') }))}
                placeholder="30"
                inputMode="numeric"
              />
              {fieldErrors.reminderDays ? <span className="field-error">{fieldErrors.reminderDays}</span> : null}
            </label>

            <label className="field">
              <span className="field-label">Amount</span>
              <input
                className="input"
                value={form.amount}
                onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value.replace(/\D/g, '') }))}
                placeholder="799"
                inputMode="numeric"
              />
              {fieldErrors.amount ? <span className="field-error">{fieldErrors.amount}</span> : null}
            </label>
          </div>

          <div className="button-row">
            <button className="primary-button" type="button" onClick={handleSave}>
              <SparkIcon size={16} />
              Save service
            </button>
            <button className="secondary-button" type="button" onClick={() => setIsFormOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={deleteTargetId !== null}
        title="Delete service"
        description="This removes the catalog item from the backend and the UI immediately."
        onClose={() => setDeleteTargetId(null)}>
        <div className="field-grid form-stack">
          <p className="copy tight">
            Deleting a service is permanent. Continue only if you are sure this catalog item should disappear.
          </p>

          <div className="button-row">
            <button className="primary-button" type="button" onClick={handleDelete}>
              Delete
            </button>
            <button className="secondary-button" type="button" onClick={() => setDeleteTargetId(null)}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
